import { DateTime } from 'luxon'

import { AreaID } from '../../areas/models/area'
import { EmployeeID } from '../../employees/models/employee'
import { Shift } from '../../shifts/models/shift'

import {
  ShiftTotals,
  ZERO_TOTALS,
  addTotals,
  isCancelled,
  isFilled,
  isPublishable,
  shiftTotals,
} from './shiftMath'

/*
|--------------------------------------------------------------------------
| Aggregation
|--------------------------------------------------------------------------
|
| Folds over the schedule's shifts. Each function walks the list once and
| returns a Map keyed by the thing being summarised, so a cell or header
| can look its figure up in constant time.
*/

/** The calendar date a shift sits on, in the shift's own zone */
export const dateKey = (date: DateTime): string => date.toISODate()

export const areaDateKey = (areaId: AreaID, date: DateTime): string =>
  `${areaId}|${dateKey(date)}`

/** Items grouped by a key; items whose key is null are left out. */
export function groupBy<T, K>(
  items: Iterable<T>,
  keyOf: (item: T) => K | null | undefined,
): Map<K, T[]> {
  const groups = new Map<K, T[]>()
  for (const item of items) {
    const key = keyOf(item)
    if (key == null) continue

    const group = groups.get(key)
    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }

  return groups
}

export function countBy<T, K>(
  items: Iterable<T>,
  keyOf: (item: T) => K | null | undefined,
): Map<K, number> {
  const counts = new Map<K, number>()
  for (const item of items) {
    const key = keyOf(item)
    if (key == null) continue

    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  return counts
}

/** Worked minutes and cost per key. */
export function aggregateBy<K>(
  shifts: Iterable<Shift>,
  keyOf: (shift: Shift) => K | null | undefined,
): Map<K, ShiftTotals> {
  const totals = new Map<K, ShiftTotals>()
  for (const shift of shifts) {
    const key = keyOf(shift)
    if (key == null) continue

    totals.set(
      key,
      addTotals(totals.get(key) ?? ZERO_TOTALS, shiftTotals(shift)),
    )
  }

  return totals
}

export const aggregateByDate = (shifts: Iterable<Shift>) =>
  aggregateBy(shifts, (shift) => dateKey(shift.startDate))

export const aggregateByArea = (shifts: Iterable<Shift>) =>
  aggregateBy(shifts, (shift) => shift.areaId)

export const aggregateByAreaDate = (shifts: Iterable<Shift>) =>
  aggregateBy(shifts, (shift) => areaDateKey(shift.areaId, shift.startDate))

export const aggregateByEmployee = (shifts: Iterable<Shift>) =>
  aggregateBy<EmployeeID>(shifts, (shift) => shift.rosterEmployeeId)

/** Every summary the schedule's headers and cells read, from one pass over the shifts. */
export interface ScheduleTotals {
  byDate: Map<string, ShiftTotals>
  filledByDate: Map<string, ShiftTotals>
  byAreaDate: Map<string, ShiftTotals>
  filledByAreaDate: Map<string, ShiftTotals>
  byEmployee: Map<EmployeeID, ShiftTotals>
}

/** The totals of a by-date map over some dates, such as the visible range. */
export function totalsOver(
  byDate: ReadonlyMap<string, ShiftTotals>,
  dates: readonly DateTime[],
): ShiftTotals {
  let total: ShiftTotals = ZERO_TOTALS
  for (const date of dates) {
    const day = byDate.get(dateKey(date))
    if (day) total = addTotals(total, day)
  }

  return total
}

/** Cancelled shifts are left out: they are shown for reference, not worked. */
export function scheduleTotals(shifts: readonly Shift[]): ScheduleTotals {
  const active = shifts.filter((shift) => !isCancelled(shift))
  const filled = active.filter(isFilled)

  return {
    byDate: aggregateByDate(active),
    filledByDate: aggregateByDate(filled),
    byAreaDate: aggregateByAreaDate(active),
    filledByAreaDate: aggregateByAreaDate(filled),
    byEmployee: aggregateByEmployee(filled),
  }
}

/** Shifts that can be published, grouped by their area. */
export function publishableByArea(
  shifts: Iterable<Shift>,
): Map<AreaID, Shift[]> {
  const publishable: Shift[] = []
  for (const shift of shifts) {
    if (isPublishable(shift)) publishable.push(shift)
  }

  return groupBy(publishable, (shift) => shift.areaId)
}

export function countUnpublished(shifts: Iterable<Shift>): number {
  let count = 0
  for (const shift of shifts) {
    if (!shift.published && !isCancelled(shift)) count += 1
  }

  return count
}
